import { createSlice } from '@reduxjs/toolkit';
import { getTopGenres } from '../../utils/genre';
import { getGroupYears } from '../../utils/year';

// Extract a 4-digit year from any of the release-date shapes we've seen
// in the wild: "2017", 2017, 2017.0, "2017.0", "2017-01-01", "2017/05/15".
// new Date() chokes on "2017.0", so it would silently drop those tracks
// from the year chart — hence the regex fallback.
function extractYearInt(releaseDate) {
    if (releaseDate == null || releaseDate === '') return NaN;
    const m = String(releaseDate).match(/\d{4}/);
    if (!m) return NaN;
    const n = parseInt(m[0], 10);
    return n > 1900 && n < 2200 ? n : NaN;
}

const initialState = {
    infoProgress: 0,
    infoIsProcessing: false,
    infoFiles: [],
    infoErrorMessage: '',
    appendFileNameBeginning: false,
    enrichedFiles: [],
    trackDetailsMap: {},
    yearData: {},
    genreData: {},
    groupYears: [],
    topGenres: [],
    chartsVisible: true,
    sortedFiles: [],
    sortDirection: 'asc',
    activeColumn: null,
    autoAnalyze: false,
    selectedPlaylistName: null,
    // Files an analysis run is working on RIGHT NOW → the fields it is
    // computing for each, e.g. { '/a/b.mp3': ['bpm', 'key'] }. Only the
    // in-flight handful (the worker pool's width) is ever in here, so the list
    // marks the rows actually being analyzed instead of the whole run. Purely
    // transient view state — never persisted.
    analyzingFiles: {},
};


const infoSlice = createSlice({
    name: 'info',
    initialState,
    reducers: {
        setInfoProgress(state, action) {
            state.infoProgress = action.payload;
        },
        setInfoIsProcessing(state, action) {
            state.infoIsProcessing = action.payload;
        },
        resetInfoFilePath(state) {
            state.infoFiles = [];
        },
        setInfoFiles(state, action) {
            state.infoFiles = action.payload;
        },
        setInfoErrorMessage(state, action) {
            state.infoErrorMessage = action.payload;
        },

        addEnrichedFile(state, action) {
            const { file, fileName, sortOrder, result } = action.payload;
            const track = result.track;

            if (!state.trackDetailsMap[file]) {
                // Set to track or empty object to prevent duplicates
                state.trackDetailsMap[file] = track || {};
                state.enrichedFiles.push({ file, fileName, sortOrder, result });
                state.sortedFiles.push({ file, fileName, sortOrder, result });

                // Skip chart updates during streaming - will be computed at end
                // This keeps the UI responsive
                if (track && !result.isLoading) {
                    // Just accumulate data, don't compute charts
                    const yearNumber = extractYearInt(track.releaseDate);
                    if (Number.isInteger(yearNumber)) {
                        const year = yearNumber.toString();
                        state.yearData[year] = (state.yearData[year] || 0) + 1;
                    }

                    const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                    if (genre) {
                        state.genreData[genre] = (state.genreData[genre] || 0) + 1;
                    }
                }
            } else {
                // File exists - update it
                state.trackDetailsMap[file] = track || {};
                const enrichedIndex = state.enrichedFiles.findIndex(item => item.file === file);
                if (enrichedIndex !== -1) {
                    state.enrichedFiles[enrichedIndex] = { ...state.enrichedFiles[enrichedIndex], file, fileName, result };
                }
                const sortedIndex = state.sortedFiles.findIndex(item => item.file === file);
                if (sortedIndex !== -1) {
                    state.sortedFiles[sortedIndex] = { ...state.sortedFiles[sortedIndex], file, fileName, result };
                }

                // Accumulate chart data
                if (track && !result.isLoading) {
                    const yearNumber = extractYearInt(track.releaseDate);
                    if (Number.isInteger(yearNumber)) {
                        const year = yearNumber.toString();
                        state.yearData[year] = (state.yearData[year] || 0) + 1;
                    }

                    const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                    if (genre) {
                        state.genreData[genre] = (state.genreData[genre] || 0) + 1;
                    }
                }
            }
        },
        // Batch add files - single dispatch for many files (much faster)
        addEnrichedFilesBatch(state, action) {
            const files = action.payload; // Array of { file, fileName, result }

            files.forEach(({ file, fileName, sortOrder, result }) => {
                // Skip macOS AppleDouble sidecars ("._Foo.mp3") + dotfiles picked up
                // when scanning folders on non-native volumes.
                const base = (fileName || file || '').split(/[\\/]/).pop() || '';
                if (base.startsWith('.')) return;
                if (!state.trackDetailsMap[file]) {
                    const track = result.track;
                    state.trackDetailsMap[file] = track || {};
                    // Keep sortOrder — the "#" column/sort and drag-reorder read it.
                    state.enrichedFiles.push({ file, fileName, sortOrder, result });
                    state.sortedFiles.push({ file, fileName, sortOrder, result });

                    // Tally chart aggregates in this same pass. On cold start the
                    // library streams in via this reducer, so accumulating here means
                    // we never need a separate full recomputeGraphData() scan over the
                    // whole library — the counts are already built by the time it's loaded.
                    if (track && !result.isLoading && !result.isPending) {
                        const yearNumber = extractYearInt(track.releaseDate);
                        if (Number.isInteger(yearNumber)) {
                            const year = yearNumber.toString();
                            state.yearData[year] = (state.yearData[year] || 0) + 1;
                        }

                        const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                        if (genre) {
                            state.genreData[genre] = (state.genreData[genre] || 0) + 1;
                        }
                    }
                }
            });

            // Derive the chart series from the (small) aggregate maps — O(distinct
            // years/genres), not O(tracks). Cheap enough to run per batch.
            if (Object.keys(state.yearData).length > 0) {
                state.groupYears = getGroupYears(state.yearData);
            }
            if (Object.keys(state.genreData).length > 0) {
                state.topGenres = getTopGenres(state.genreData);
            }
        },
        // Batch update files - single dispatch for many cached results (much faster)
        updateEnrichedFilesBatch(state, action) {
            const files = action.payload; // Array of { file, fileName, result }

            // Build index maps for O(1) lookups instead of O(n) findIndex
            const enrichedIndexMap = {};
            state.enrichedFiles.forEach((item, index) => {
                enrichedIndexMap[item.file] = index;
            });
            const sortedIndexMap = {};
            state.sortedFiles.forEach((item, index) => {
                sortedIndexMap[item.file] = index;
            });

            files.forEach(({ file, fileName, result }) => {
                const track = result.track;

                // Update trackDetailsMap
                state.trackDetailsMap[file] = track;

                // Update in enrichedFiles using index map — spread the existing item
                // first so sortOrder (used by the "#" column/sort) isn't wiped.
                const enrichedIndex = enrichedIndexMap[file];
                const sortedIndex = sortedIndexMap[file];
                // Carry the missing-file flag across result rebuilds — analysis /
                // enrichment payloads don't know about it, and replacing `result`
                // wholesale would silently un-flag a not-on-this-device row.
                const prev = enrichedIndex !== undefined ? state.enrichedFiles[enrichedIndex]
                    : (sortedIndex !== undefined ? state.sortedFiles[sortedIndex] : undefined);
                const mergedResult = (prev?.result?.fileNotFound === true && result.fileNotFound === undefined)
                    ? { ...result, fileNotFound: true }
                    : result;
                if (enrichedIndex !== undefined) {
                    state.enrichedFiles[enrichedIndex] = { ...state.enrichedFiles[enrichedIndex], file, fileName, result: mergedResult };
                }

                // Update in sortedFiles using index map
                if (sortedIndex !== undefined) {
                    state.sortedFiles[sortedIndex] = { ...state.sortedFiles[sortedIndex], file, fileName, result: mergedResult };
                }

                // Update graph data if track has valid data
                if (track && !result.isLoading) {
                    // Update year data — skip falsy values to avoid epoch (1970) from null/empty
                    const yearNumber = extractYearInt(track.releaseDate);
                    if (Number.isInteger(yearNumber)) {
                        const year = yearNumber.toString();
                        state.yearData[year] = (state.yearData[year] || 0) + 1;
                    }

                    // Update genre data
                    const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                    if (genre) {
                        state.genreData[genre] = (state.genreData[genre] || 0) + 1;
                    }
                }
            });

            // Compute charts once at the end
            if (Object.keys(state.yearData).length > 0) {
                state.groupYears = getGroupYears(state.yearData);
            }
            if (Object.keys(state.genreData).length > 0) {
                state.topGenres = getTopGenres(state.genreData);
            }
        },
        updateEnrichedFile(state, action) {
            const { file, fileName, result } = action.payload;
            const track = result.track;

            // Update trackDetailsMap
            state.trackDetailsMap[file] = track;

            // Find and update in enrichedFiles (keep sortOrder)
            const enrichedIndex = state.enrichedFiles.findIndex(item => item.file === file);
            const sortedIndex = state.sortedFiles.findIndex(item => item.file === file);
            // Preserve the missing-file flag across result rebuilds (see
            // updateEnrichedFilesBatch).
            const prev = enrichedIndex !== -1 ? state.enrichedFiles[enrichedIndex]
                : (sortedIndex !== -1 ? state.sortedFiles[sortedIndex] : undefined);
            const mergedResult = (prev?.result?.fileNotFound === true && result.fileNotFound === undefined)
                ? { ...result, fileNotFound: true }
                : result;
            if (enrichedIndex !== -1) {
                state.enrichedFiles[enrichedIndex] = { ...state.enrichedFiles[enrichedIndex], file, fileName, result: mergedResult };
            }

            // Find and update in sortedFiles (keep sortOrder)
            if (sortedIndex !== -1) {
                state.sortedFiles[sortedIndex] = { ...state.sortedFiles[sortedIndex], file, fileName, result: mergedResult };
            }

            // Update graph data if track has valid data
            if (track && !result.isLoading) {
                // Update year data dynamically
                const yearNumber = track.releaseDate ? new Date(track.releaseDate)?.getFullYear() : NaN;
                if (Number.isInteger(yearNumber) && yearNumber > 1900) {
                    const year = yearNumber.toString();
                    if (state.yearData[year]) {
                        state.yearData[year] += 1;
                    } else {
                        state.yearData[year] = 1;
                    }

                    state.groupYears = getGroupYears(state.yearData);
                }

                // Update genre data dynamically
                const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                if (genre) {
                    if (state.genreData[genre]) {
                        state.genreData[genre] += 1;
                    } else {
                        state.genreData[genre] = 1;
                    }

                    state.topGenres = getTopGenres(state.genreData);
                }
            }
        },
        setChartsVisible(state, action) {
            state.chartsVisible = action.payload;
        },
        // Files that entered / left the worker pool since the last tick. The
        // renderer coalesces the enricher's per-file events into one of these
        // every ~120ms — a warm run starts and finishes dozens of files a
        // second, and a dispatch each would re-render the list on every one.
        applyAnalyzingDelta(state, action) {
            const { add = [], remove = [] } = action.payload || {};
            remove.forEach((file) => { delete state.analyzingFiles[file]; });
            add.forEach(({ file, fields }) => {
                if (file) state.analyzingFiles[file] = fields && fields.length ? fields : [];
            });
        },
        // End of a run (finished, stopped or failed) — nothing is in flight.
        clearAnalyzingFiles(state) {
            state.analyzingFiles = {};
        },
        resetEnrichedFiles(state) {
            state.analyzingFiles = {};
            state.enrichedFiles = [];
            state.trackDetailsMap = {};
            state.yearData = {};
            state.genreData = {};
            state.groupYears = [];
            state.topGenres = [];
            state.sortedFiles = [];
        },
        // Account switch: drop everything that describes the previous
        // account's library (the rows, the charts built from them, the
        // playlist scope, any in-flight selection) but keep view preferences
        // (chartsVisible, autoAnalyze, sort) — those are the person's, not
        // the account's. VisualizeLibraryTab only refetches from the
        // owner-scoped DB when enrichedFiles is EMPTY, so leaving the old rows
        // here meant the next account was shown them as its own library.
        resetInfoForAccountSwitch(state) {
            state.analyzingFiles = {};
            state.enrichedFiles = [];
            state.trackDetailsMap = {};
            state.yearData = {};
            state.genreData = {};
            state.groupYears = [];
            state.topGenres = [];
            state.sortedFiles = [];
            state.infoFiles = [];
            state.infoProgress = 0;
            state.infoIsProcessing = false;
            state.infoErrorMessage = '';
            state.selectedPlaylistName = null;
        },
        removeEnrichedFiles(state, action) {
            const paths = new Set(action.payload || []);
            if (paths.size === 0) return;
            state.enrichedFiles = state.enrichedFiles.filter(f => !paths.has(f.file));
            state.sortedFiles = (state.sortedFiles || []).filter(f => !paths.has(f.file));
            // Clear the trackDetailsMap entries too — otherwise addEnrichedFilesBatch's
            // `if (!trackDetailsMap[file])` guard silently skips a later re-add of the
            // same path (delete a track, drag it back in → it never reappears).
            for (const p of paths) delete state.trackDetailsMap[p];
        },
        markEnrichedFilesMissing(state, action) {
            // payload: string[] of file paths that no longer exist on disk, OR
            // { missing: string[], checked: string[] } — `checked` is the full
            // set of paths that were fs-verified, so rows that were checked and
            // found present get a stale fileNotFound flag CLEARED (file
            // restored, external drive remounted).
            const payload = action.payload || [];
            const missingArr = Array.isArray(payload) ? payload : (payload.missing || []);
            const checkedArr = Array.isArray(payload) ? null : (payload.checked || null);
            const missing = new Set(missingArr);
            const checked = checkedArr ? new Set(checkedArr) : null;
            if (missing.size === 0 && !checked) return;
            const stamp = (arr) => {
                for (const f of arr) {
                    if (missing.has(f.file)) {
                        if (!f.result) f.result = {};
                        f.result.fileNotFound = true;
                    } else if (checked && checked.has(f.file) && f.result?.fileNotFound) {
                        f.result.fileNotFound = false;
                    }
                }
            };
            stamp(state.enrichedFiles);
            if (state.sortedFiles) stamp(state.sortedFiles);
        },
        relocateEnrichedFiles(state, action) {
            // payload: { [oldPath]: newPath } — updates file path and clears fileNotFound
            const map = action.payload || {};
            const keys = Object.keys(map);
            if (keys.length === 0) return;
            const rewrite = (arr) => {
                for (const f of arr) {
                    const next = map[f.file];
                    if (!next) continue;
                    f.file = next;
                    const fname = next.split(/[\\/]/).pop();
                    if (fname) f.fileName = fname;
                    if (f.result) f.result.fileNotFound = false;
                }
            };
            rewrite(state.enrichedFiles);
            if (state.sortedFiles) rewrite(state.sortedFiles);
        },
        setSortedFiles(state, action) {
            state.sortedFiles = action.payload;
        },
        // Reconcile the RENDERED list (sortedFiles) with the master enrichedFiles.
        // Live enrichment only patches sortedFiles rows that already exist there,
        // so a file that landed in enrichedFiles (and the DB) but whose sortedFiles
        // copy is missing/stale would show blank until a remount re-read the DB —
        // e.g. a freshly-fetched cover not appearing until you leave and re-enter
        // the Library tab. This syncs them in-memory (no DB round-trip): refresh
        // present rows' result, append any missing. O(enrichedFiles), run once at
        // end-of-run alongside recomputeGraphData.
        syncSortedFromEnriched(state) {
            const sortedIndex = {};
            state.sortedFiles.forEach((f, i) => { if (f && f.file) sortedIndex[f.file] = i; });
            state.enrichedFiles.forEach((ef) => {
                if (!ef || !ef.file) return;
                const i = sortedIndex[ef.file];
                if (i === undefined) {
                    state.sortedFiles.push({ ...ef });
                } else {
                    state.sortedFiles[i] = { ...state.sortedFiles[i], fileName: ef.fileName, result: ef.result };
                }
            });
        },
        setSortDirection(state, action) {
            state.sortDirection = action.payload;
        },
        setActiveColumn(state, action) {
            state.activeColumn = action.payload;
        },
        recomputeGraphData(state) {
            // Rebuild yearData and genreData from enrichedFiles, then compute charts
            // This is useful when loading from cache where yearData/genreData aren't persisted
            state.yearData = {};
            state.genreData = {};

            // Rebuild from enrichedFiles
            state.enrichedFiles.forEach(({ result }) => {
                const track = result?.track;
                if (track) {
                    // Extract and count year — skip falsy values to avoid epoch (1970) from null/empty
                    const yearNumber = extractYearInt(track.releaseDate);
                    if (Number.isInteger(yearNumber)) {
                        const year = yearNumber.toString();
                        state.yearData[year] = (state.yearData[year] || 0) + 1;
                    }

                    // Extract and count genre
                    const genre = track.styles?.[0] || track.genres?.[0] || track.genre;
                    if (genre) {
                        state.genreData[genre] = (state.genreData[genre] || 0) + 1;
                    }
                }
            });

            // Now compute the chart data
            if (Object.keys(state.yearData).length > 0) {
                state.groupYears = getGroupYears(state.yearData);
            }

            if (Object.keys(state.genreData).length > 0) {
                state.topGenres = getTopGenres(state.genreData);
            }
        },
        setInfoAutoAnalyze(state, action) {
            state.autoAnalyze = action.payload;
        },
        setInfoPlaylistName(state, action) {
            state.selectedPlaylistName = action.payload;
        }
    },
});

export const {
    setInfoProgress,
    setInfoIsProcessing,
    resetInfoFilePath,
    setInfoFiles,
    setInfoErrorMessage,
    addEnrichedFile,
    addEnrichedFilesBatch,
    updateEnrichedFile,
    updateEnrichedFilesBatch,
    resetEnrichedFiles,
    resetInfoForAccountSwitch,
    applyAnalyzingDelta,
    clearAnalyzingFiles,
    removeEnrichedFiles,
    relocateEnrichedFiles,
    markEnrichedFilesMissing,
    setChartsVisible,
    setSortedFiles,
    setSortDirection,
    setActiveColumn,
    recomputeGraphData,
    syncSortedFromEnriched,
    setInfoAutoAnalyze,
    setInfoPlaylistName
} = infoSlice.actions;

export default infoSlice.reducer;
